import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import GachaFrame, { asset, srcSet } from './GachaFrame'
import { drawGacha, fetchStatus, GachaResult, GachaStatus } from './gachaApi'
import { toastUtil } from '../../../utils/toast'
import { RECORD_NAV_ITEM } from '../../../types/navItem'

/**
 * QR 캡슐 뽑기 (/event/gacha).
 *
 * 동작·타이밍은 ~/tastee-gacha-preview/index.html 을 그대로 옮겼다.
 *   ② 레버 0.6s → 흔들림 3px×4 → 캡슐 섞임, 총 ~1.5s
 *   ③ 출구 → 화면 중앙 확대 0.5s → 맥동·반짝이 1s
 *   ④ 열림 0.4s → 결과 카드 (당첨만 빛·꽃가루)
 * 결과는 서버(gachaApi)가 정한다. 진동(햅틱)은 첫 오픈에서 뺐다.
 * 결과 카드는 HTML/CSS 로 그린다(원래 카드 PNG 의 색·모양을 따름).
 */

// 개발 모드에서만 불러온다. 운영 빌드에서는 이 분기가 사라져 스위치가 번들에 안 들어간다.
const IS_DEV = process.env.NODE_ENV === 'development'
const Debug: typeof import('./GachaDebug') | null =
  // require 조건은 process.env 를 직접 써야 webpack 이 빌드 때 잘라낸다(IS_DEV 로는 안 잘림)
  process.env.NODE_ENV === 'development' ? require('./GachaDebug') : null

const CAPSULE_POS: [number, number][] = [
  [60, 70], [110, 45], [165, 60], [205, 95], [85, 120], [140, 110], [190, 140],
]
const CONFETTI_KINDS = ['circle', 'rect', 'squiggle']
const CONFETTI_COLORS = ['beige', 'cream', 'dark', 'mid']

type Phase = 'idle' | 'shuffle' | 'zoom' | 'pulse' | 'open' | 'card' | 'done'
type Confetti = { id: number; src: string; style: React.CSSProperties }

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

const GachaEvent: React.FC = () => {
  const navigate = useNavigate()
  // 뽑을 수 없으면(운영 빌드 mock 포함) 버튼만 비활성. 따로 안내 화면은 없다.
  const [canDraw, setCanDraw] = useState(false)
  // 꽝 카드에 쓰는 '다음 뽑기까지' — 뽑은 뒤 서버에서 다시 받는다
  const [status, setStatus] = useState<GachaStatus | null>(null)
  const [phase, setPhase] = useState<Phase>('idle')
  const [result, setResult] = useState<GachaResult | null>(null)
  const [confetti, setConfetti] = useState<Confetti[]>([])
  const bigRef = useRef<HTMLDivElement>(null)
  const alive = useRef(true)
  const autoplayed = useRef(false)
  // 개발 모드: ?debug= 는 첫 상태를 받기 전(첫 렌더)에 반영돼야 한다
  const [autoplay] = useState(() => (IS_DEV && Debug ? Debug.applyGachaParams().autoplay : false))

  const loadStatus = useCallback(() => {
    fetchStatus().then((s) => {
      if (!alive.current) return
      setStatus(s)
      setCanDraw(!!s?.canDraw)
    })
  }, [])

  useEffect(() => {
    alive.current = true
    loadStatus()
    return () => {
      alive.current = false
    }
  }, [loadStatus])

  const reset = () => {
    bigRef.current?.removeAttribute('style')
    setPhase('idle')
    setResult(null)
    setConfetti([])
  }

  const play = useCallback(async () => {
    if (!canDraw) return
    // 결과는 레버를 누르는 순간 서버에 먼저 받아 두고 ④에서 보여준다
    const pending = drawGacha()
    setPhase('shuffle')
    await sleep(1500)
    if (!alive.current) return

    setPhase('zoom')
    const big = bigRef.current
    if (!big) return
    big.style.cssText = 'opacity:1;transform:translate(240px,555px) scale(.75)'
    await sleep(30)
    big
      .animate(
        [
          { transform: 'translate(240px,555px) scale(.75) rotate(0)' },
          { transform: 'translate(190px,575px) scale(.8) rotate(-200deg)', offset: 0.3 },
          { transform: 'translate(165px,350px) scale(2.6) rotate(-360deg)', offset: 0.85 },
          { transform: 'translate(165px,370px) scale(2.3) rotate(-360deg)' },
        ],
        { duration: 500, easing: 'ease-out', fill: 'forwards' },
      )
      .finished.then(() => {
        big.getAnimations().forEach((a) => a.cancel())
        big.style.transform = 'translate(165px,370px) scale(2.3)'
      })
      .catch(() => {})
    await sleep(520)
    if (!alive.current) return

    setPhase('pulse')
    await sleep(1000)
    let r: GachaResult
    try {
      r = await pending
    } catch (e: any) {
      if (!alive.current) return
      toastUtil.error(e?.response?.data?.message || '잠시 후 다시 시도해주세요')
      reset()
      loadStatus()
      return
    }
    if (!alive.current) return
    loadStatus()

    setResult(r)
    setPhase('open')
    await sleep(400)
    if (!alive.current) return

    setPhase('card')
    if (r === 'win') {
      setConfetti(
        Array.from({ length: 60 }, (_, i) => ({
          id: Date.now() + i,
          src: asset(`confetti-${CONFETTI_KINDS[i % 3]}-${CONFETTI_COLORS[i % 4]}`),
          style: {
            left: `${Math.random() * 390}px`,
            ['--x' as any]: `${(Math.random() - 0.5) * 120}px`,
            ['--r' as any]: `${Math.random() * 720}deg`,
            animationDelay: `${Math.random() * 0.3}s`,
          },
        })),
      )
      setTimeout(() => alive.current && setConfetti([]), 2000)
    }
    await sleep(500)
    if (alive.current) setPhase('done')
  }, [canDraw, loadStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  // 개발 모드: ?autoplay=1 이면 상태를 받자마자 한 번 돌린다(스크린샷용)
  useEffect(() => {
    if (!autoplay || autoplayed.current || !canDraw) return
    autoplayed.current = true
    setTimeout(play, 600)
  }, [autoplay, canDraw, play])

  const playing = phase !== 'idle'
  const stage3On = phase !== 'idle' && phase !== 'shuffle'
  const opened = phase === 'open' || phase === 'card' || phase === 'done'
  const isWin = result === 'win'

  return (
    <GachaFrame>
      {IS_DEV && Debug && (
        <Debug.GachaDebugBar
          onChange={() => {
            reset()
            loadStatus()
          }}
        />
      )}
      <h1>TASTEE 캡슐 뽑기</h1>
      <p className="g-sub">기록 3개면 1번, 5개면 2번 뽑을 수 있어요</p>

      <div className={`g-machine${phase === 'shuffle' ? ' shake' : ''}`}>
        <img className="g-body" src={asset('machine-body')} srcSet={srcSet('machine-body')} alt="" />
        <div className={`g-caps${phase === 'shuffle' ? ' shuffle' : ''}`}>
          {CAPSULE_POS.map(([x, y], i) => (
            <img
              key={i}
              className="g-cap"
              src={asset(`capsule-${i + 1}`)}
              srcSet={srcSet(`capsule-${i + 1}`)}
              alt=""
              style={{
                position: 'absolute',
                left: x,
                top: y,
                ['--a' as any]: `${2 + (i % 3)}px`,
                ['--p' as any]: `${2 + ((i * 0.17) % 1)}s`,
                ['--d' as any]: `-${i * 0.37}s`,
                ['--s' as any]: `${i % 2 ? -14 : 14}px`,
                ['--sd' as any]: `${0.6 + i * 0.03}s`,
              }}
            />
          ))}
        </div>
        <img className="g-dome" src={asset('glass-dome-front')} srcSet={srcSet('glass-dome-front')} alt="" />
        <img
          className={`g-knob${phase === 'shuffle' ? ' spin' : ''}`}
          src={asset('knob')}
          srcSet={srcSet('knob')}
          alt=""
        />
      </div>

      {/*
        뽑기 전 안내 (2026-11-04 도연님): 못 뽑으면 '몇 개 더'를 보여주고 버튼은 기록하러 보낸다.
        다 썼으면 그렇다고, 당첨됐으면 교환권으로. 뽑는 중·결과 화면에서는 숨긴다.
      */}
      {phase === 'idle' && status?.eligible && !canDraw && (
        <div className="g-need">
          {status.hasVoucher ? (
            <p className="g-need-t">이미 당첨됐어요! 교환권을 확인해 주세요</p>
          ) : status.recordsToNext !== null && status.nextAt ? (
            <>
              <p className="g-need-t">
                기록 <b>{status.recordsToNext}개</b> 더 쓰면 뽑을 수 있어요
              </p>
              <div className="g-bar">
                <i style={{ width: `${Math.min(100, (status.records / status.nextAt) * 100)}%` }} />
              </div>
              <p className="g-need-c">
                {status.records} / {status.nextAt}
              </p>
            </>
          ) : (
            <p className="g-need-t">이번 이벤트 뽑기를 모두 사용했어요</p>
          )}
        </div>
      )}
      {phase === 'idle' && status?.eligible && canDraw && status.drawsLeft > 0 && (
        <div className="g-need">
          <p className="g-need-t">
            뽑기 <b>{status.drawsLeft}번</b> 남았어요
          </p>
        </div>
      )}

      {phase === 'idle' && status?.eligible && !canDraw ? (
        status.hasVoucher ? (
          <button className="g-btn" onClick={() => navigate('/event/gacha/voucher')}>
            교환권 보기
          </button>
        ) : status.recordsToNext !== null ? (
          <button className="g-btn" onClick={() => navigate(RECORD_NAV_ITEM.path)}>
            기록하러 가기
          </button>
        ) : (
          <button className="g-btn" onClick={() => navigate('/main-home')}>
            홈으로
          </button>
        )
      ) : (
        <button className="g-btn" disabled={!canDraw || playing} onClick={play}>
          레버 돌리기
        </button>
      )}

      <div className={`g-stage3${stage3On ? ' on' : ''}`}>
        <div className={`g-drawing${opened ? ' hide' : ''}`}>뽑기 중…</div>
        <img
          className={`g-burst${opened && isWin ? ' on' : ''}`}
          src={asset('light-burst-strong')}
          srcSet={srcSet('light-burst-strong')}
          alt=""
        />
        <img
          className={`g-winspark${opened && isWin ? ' on' : ''}`}
          src={asset('sparkle')}
          srcSet={srcSet('sparkle')}
          alt=""
        />
        <div ref={bigRef} className={`g-big${phase === 'pulse' ? ' pulse' : ''}${opened ? ' open' : ''}`}>
          <img className="b" src={asset('capsule-bottom')} srcSet={srcSet('capsule-bottom')} alt="" />
          <img className="t" src={asset('capsule-top')} srcSet={srcSet('capsule-top')} alt="" />
        </div>
        {phase === 'pulse' && (
          <div className="g-orbit">
            {[0, 90, 180, 270].map((a, i) => {
              const t = (a * Math.PI) / 180
              return (
                <img
                  key={a}
                  className="g-spark"
                  src={asset('sparkle')}
                  alt=""
                  style={{ left: Math.cos(t) * 85, top: Math.sin(t) * 85, animationDelay: `${i * 0.12}s` }}
                />
              )
            })}
          </div>
        )}
        {result && (
          <div className={`g-card ${result}${phase === 'card' || phase === 'done' ? ' on' : ''}`}>
            {isWin ? (
              <>
                <span className="g-badge">당첨</span>
                <p className="g-title">후라이드치킨 1마리 당첨</p>
                <button className="g-cbtn" onClick={() => navigate('/event/gacha/voucher')}>
                  교환 방법 보기
                </button>
              </>
            ) : (
              <>
                <p className="g-title">아쉽지만 다음 기회에!</p>
                {/* 다음 뽑기: 이미 남았으면 바로, 기록이 더 필요하면 진행바, 다 썼으면 끝 */}
                {status && status.drawsLeft > 0 ? (
                  <>
                    <p className="g-desc">한 번 더 뽑을 수 있어요</p>
                    <button className="g-cbtn" onClick={reset}>
                      한 번 더 뽑기
                    </button>
                  </>
                ) : status && status.recordsToNext !== null && status.nextAt ? (
                  <>
                    <p className="g-desc">기록 {status.recordsToNext}개 더 채우면 한 번 더 뽑을 수 있어요</p>
                    <div className="g-bar">
                      <i style={{ width: `${Math.min(100, (status.records / status.nextAt) * 100)}%` }} />
                    </div>
                    <p className="g-count">
                      {status.records} / {status.nextAt}
                    </p>
                    <button className="g-cbtn" onClick={() => navigate(RECORD_NAV_ITEM.path)}>
                      기록하러 가기
                    </button>
                  </>
                ) : (
                  <>
                    <p className="g-desc">이번 이벤트 뽑기를 모두 사용했어요</p>
                    <button className="g-cbtn" onClick={() => navigate('/main-home')}>
                      홈으로
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}
        {confetti.map((c) => (
          <img key={c.id} className="g-cf" src={c.src} style={c.style} alt="" />
        ))}
      </div>
    </GachaFrame>
  )
}

export default GachaEvent
